import { faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useTracking, { TrackingEventData } from '../../../hooks/useTracking';
import { notEmpty } from '../../../utils/notEmpty';
import { EventNames } from '../../../utils/trackingEvents';
import { ButtonSize, ButtonVariant } from '../Button';
import { IconButton } from '../Button/IconButton';
import { LinkButton } from '../Button/LinkButton';
import { Tag } from '../TagGroup';
import Text, { TextKind } from '../Text';
import { SizeProp } from '../util';

interface FilterItem {
  label: string;
  value: unknown;
}

interface AppliedFiltersProps<T> {
  name?: string;
  appliedFilters: unknown[];
  allFilters: T[];
  removeFilter: (filterOption: T) => void;
  clearAllFilters: () => void;
  size?: SizeProp;
  trackingData?: TrackingEventData;
}

export const AppliedFilters = <T extends FilterItem>({
  name,
  appliedFilters,
  allFilters,
  removeFilter,
  clearAllFilters,
  trackingData,
  size = SizeProp.Medium,
}: AppliedFiltersProps<T>) => {
  const { track } = useTracking();

  const handleRemoveFilter = (filterOption: T) => {
    if (trackingData) {
      track(EventNames.RemoveTag, {
        ...trackingData,
        event_label: filterOption.label,
      });
    }
    removeFilter(filterOption);
  };

  const handleClearAllFilters = () => {
    if (trackingData) {
      track(EventNames.ClearAll, {
        ...trackingData,
      });
    }
    clearAllFilters();
  };

  return (
    <div
      data-testid={`${name}-applied-filters`}
      style={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
      }}
    >
      {appliedFilters
        .map((filter) =>
          allFilters.find((filterOption) => filterOption.value === filter),
        )
        .filter(notEmpty)
        .map((filterOption) => (
          <Tag
            key={filterOption.value + filterOption.label}
            size={size}
            testId={`${name}-applied-filter-option`}
          >
            <Text
              kind={
                {
                  [SizeProp.Small]: TextKind.BodyS,
                  [SizeProp.Medium]: TextKind.BodyM,
                }[size]
              }
            >
              {filterOption.label}
            </Text>{' '}
            <IconButton
              size={ButtonSize.ExtraSmall}
              testId={`${name}-applied-filter-option-clear`}
              onClick={() => handleRemoveFilter(filterOption)}
              variant={ButtonVariant.Transparent}
              icon={<FontAwesomeIcon icon={faXmark} width={8} />}
            />
          </Tag>
        ))}
      <LinkButton
        testId={`${name}-applied-filter-option-clearAll`}
        onClick={handleClearAllFilters}
      >
        <Text
          kind={
            {
              [SizeProp.Small]: TextKind.BodyS,
              [SizeProp.Medium]: TextKind.BodyM,
            }[size]
          }
        >
          Clear all
        </Text>
      </LinkButton>
    </div>
  );
};
